import { Image } from '@apparel/core/models/Image';
import { hash } from '@apparel/core/utils/hash';
import { separate } from '@apparel/core/utils/strings/separate';
import { camelCase } from 'lodash';
import { ImageVariantMap, ImageVariants } from './ImageProps';

export class ImageObject {
    constructor({ filePath = '', thumbnailPath = '', width = 0, height = 0 } = {}) {
        this.filePath = filePath;
        this.thumbnailPath = thumbnailPath;
        this.width = width;
        this.height = height;
    }

    /**
     * Source of the image
     * @private
     */
    private _filePath: string;

    /**
     * Thumbnail of the image
     * @private
     */
    private _thumbnailPath: string;

    /**
     * Width of the image
     * @private
     */
    private _width: number;

    /**
     * Height of the image
     * @private
     */
    private _height: number;

    /**
     * Computed key of the object
     * @private
     */
    private _computedKey: string;

    /**
     * Getter of the filePath
     */
    public get filePath(): string {
        return this._filePath;
    }

    /**
     * Setter of filePath property used to store value and invalidate the key
     * @param value
     */
    public set filePath(value: string) {
        this.invalidateKey();
        this._filePath = value;
    }

    /**
     * Getter of the thumbnailPath
     */
    public get thumbnailPath(): string {
        return this._thumbnailPath;
    }

    /**
     * Setter of thumbnailPath property used to store value and invalidate the key
     * @param value
     */
    public set thumbnailPath(value: string) {
        this.invalidateKey();
        this._thumbnailPath = value;
    }

    /**
     * Getter of the width
     */
    public get width(): number {
        return this._width;
    }

    /**
     * Setter of width property used to store and invalidate the key
     * @param value
     */
    public set width(value: number) {
        this.invalidateKey();
        this._width = value;
    }

    /**
     * Getter of the height
     */
    public get height(): number {
        return this._height;
    }

    /**
     * Setter of height property used to store and invalidate the key
     * @param value
     */
    public set height(value: number) {
        this.invalidateKey();
        this._height = value;
    }

    /**
     * Getter of the key
     */
    public get key() {
        if (!this._computedKey) {
            this._computedKey = this.computeKey();
        }

        return this._computedKey;
    }

    /**
     * Compute key based on the image properties
     * @private
     */
    private computeKey() {
        return hash(this._filePath + this._thumbnailPath + this._width + this._height);
    }

    /**
     * Invalidate computed key
     * @private
     */
    private invalidateKey() {
        this._computedKey = null;
    }

    public static from(source: string | Image | ImageObject) {
        if (!source) {
            return null;
        }

        if (source instanceof ImageObject) {
            return new this({
                filePath: source.filePath,
                thumbnailPath: source.thumbnailPath,
                width: source.width,
                height: source.height,
            });
        }

        if (typeof source === 'string') {
            return new this({
                filePath: source,
                thumbnailPath: source,
            });
        }

        if (typeof source === 'object') {
            return new this({
                filePath: source.file_path,
                thumbnailPath: source.thumbnail_path || source.file_path,
                width: source.width,
                height: source.height,
            });
        }
    }

    public getSource(variant: ImageVariants) {
        const props = separate(ImageVariantMap[variant], ',');
        let value: string = null;

        for (const prop of props) {
            value = this[prop] || this[camelCase(prop)];
            if (value) {
                return value;
            }
        }

        return null;
    }
}
